import { useState, useEffect, useRef } from 'react';
import type { ProjectScaleConfig } from './projects.types';

/**
 * Calculates adaptive viewport, DPI, and zoom-aware scale factors (Option B).
 * Injects CSS custom properties into the referenced project container.
 */
export const useProjectScale = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scaleConfig, setScaleConfig] = useState<ProjectScaleConfig>({
    scale: 1,
    fontScale: 1,
    cardScale: 1,
  });

  useEffect(() => {
    // * Calculates proportional scaling against 1080p 96dpi baseline
    const computeScale = (): ProjectScaleConfig => {
      try {
        if (typeof window === 'undefined') {
          return { scale: 1, fontScale: 1, cardScale: 1 };
        }

        const width = window.innerWidth;
        const height = window.innerHeight;
        const dpr = window.devicePixelRatio || 1;

        // ? Balance viewport dimensions against hardware DPI & zoom levels
        const widthFactor = width / 1920;
        const heightFactor = height / 1080;
        const baseFactor = Math.min(widthFactor, heightFactor);
        const dprNormalization = 1 / Math.sqrt(dpr);

        const computed = baseFactor * dprNormalization;
        const scale = Math.min(1.25, Math.max(0.72, parseFloat(computed.toFixed(3))));
        const cardScale = Math.min(1.18, Math.max(0.78, scale));
        const fontScale = Math.min(1.2, Math.max(0.85, parseFloat(((scale + 1) / 2).toFixed(3))));

        return { scale, fontScale, cardScale };
      } catch (err) {
        // ! Fail-safe to standard unity scaling upon any unexpected error
        console.error('[useProjectScale] Failed to compute dynamic scale:', err);
        return { scale: 1, fontScale: 1, cardScale: 1 };
      }
    };

    let rafId: number | null = null;
    const handleResize = (): void => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const next = computeScale();
        setScaleConfig(next);

        const el = containerRef.current;
        if (el) {
          el.style.setProperty('--project-scale', next.scale.toString());
          el.style.setProperty('--card-scale', next.cardScale.toString());
          el.style.setProperty('--project-font-scale', next.fontScale.toString());
        }
      });
    };

    // * Initial application
    handleResize();

    // * Register listeners for viewport resize and pinch/zoom changes
    window.addEventListener('resize', handleResize, { passive: true });
    const visualVp = window.visualViewport;
    if (visualVp) {
      visualVp.addEventListener('resize', handleResize, { passive: true });
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
      if (visualVp) {
        visualVp.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  return { containerRef, scaleConfig };
};

export default useProjectScale;
